import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "../../components/BrandLogo";
import {
 
  FiChevronDown,
  FiMenu,
  FiX,
  FiBookOpen,
  FiDollarSign,
  FiUser,
  FiLogOut,
  FiGrid,
  FiBookmark,
  FiMap,
  FiSearch,
  FiTag,
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiArrowRight,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { ICON, UI } from "../../utils/ui";
import { publicEventsApi, publicCategoriesApi } from "../../api/publicApi";
import { notify } from "../../components/Notify";
import ConfirmModal from "../../components/ConfirmModal";

const DEFAULT_MENU = [
  { name: "Home", path: "/" },
  {
    name: "Courses",
    path: "/courses",
    dropdown: [
      { icon: FiBookOpen, label: "Browse All Courses", path: "/courses" },
      { icon: FiMap, label: "Browse by Location", path: "/courses/map" },
      { icon: FiDollarSign, label: "Compare Plans", path: "/pricing" },
    ],
  },
  { name: "Pricing", path: "/pricing" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

const ROLE_DASHBOARD = {
  admin: "/admin/dashboard",
  provider: "/provider/dashboard",
  customer: "/customer/dashboard",
};

const ROLE_BADGE = {
  admin: { label: "Admin", color: "bg-[#8A9778]/15 text-[#6B7760]" },
  provider: { label: "Provider", color: "bg-[#D4A45A]/15 text-[#B8893F]" },
  customer: { label: "Member", color: "bg-[#8A9778]/15 text-[#8A9778]" },
};

const ROLE_BOOKINGS = {
  provider: "/provider/bookings",
  customer: "/customer/bookings",
};

export default function Header({ menu = [] }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({
    courses: [],
    categories: [],
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const profileRef = useRef(null);
  const searchRef = useRef(null);
  const [allCourses, setAllCourses] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  
  // Auth Modal States
  const [authModal, setAuthModal] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleConfirmLogout = async () => {
    setLoggingOut(true);
    try {
      setProfileOpen(false);
      setMobileOpen(false);
      await logout();
      setShowLogoutModal(false);
      notify.success("You have been signed out successfully.", "Logged Out");
      navigate("/login");
    } catch {
      notify.error("Failed to sign out. Please try again.");
    } finally {
      setLoggingOut(false);
    }
  };

  // Fetch courses and categories for search
  useEffect(() => {
    publicEventsApi()
      .then((res) => setAllCourses(res.data?.data ?? []))
      .catch(() => {});
    publicCategoriesApi()
      .then((res) => setAllCategories(res.data?.data ?? []))
      .catch(() => {});
  }, []);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults({ courses: [], categories: [] });
      return;
    }
    const q = searchQuery.toLowerCase();
    const timer = setTimeout(() => {
      const matchedCourses = allCourses
        .filter((c) => c.title?.toLowerCase().includes(q))
        .slice(0, 5);
      const matchedCategories = allCategories
        .filter((c) => c.name?.toLowerCase().includes(q))
        .slice(0, 3);
      setSearchResults({
        courses: matchedCourses,
        categories: matchedCategories,
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, allCourses, allCategories]);

  // Close search on click outside
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const currentMenu = menu.length > 0 ? menu : DEFAULT_MENU;
  const dashboardPath = ROLE_DASHBOARD[user?.role] ?? "/login";
  const bookingsPath = ROLE_BOOKINGS[user?.role];

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(null);
    setProfileOpen(false);
    setAuthModal(null);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/courses?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  const handleCourseClick = (id) => {
    navigate(`/courses/${id}`);
    setSearchOpen(false);
    setSearchQuery("");
  };

  const handleCategoryClick = (name) => {
    navigate(`/courses?category=${encodeURIComponent(name)}`);
    setSearchOpen(false);
    setSearchQuery("");
  };

  const navCls = ({ isActive }) =>
    `relative text-sm font-medium transition-colors duration-200 after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:rounded-full after:bg-[#8A9778] after:transition-all after:duration-300 ${
      isActive
        ? "text-[#6B7760] after:w-full"
        : "text-slate-600 hover:text-[#8A9778] after:w-0 hover:after:w-full"
    }`;

  const iconWrap =
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F8F5F0] text-[#8A9778] transition group-hover:bg-[#8A9778]/10";

  // Auth handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    try {
      // Add your login logic here
      console.log("Login:", loginData);
      setAuthModal(null);
    } catch (err) {
      setAuthError(err.message || "Login failed");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthError("");
    if (signupData.password !== signupData.confirmPassword) {
      setAuthError("Passwords do not match");
      return;
    }
    setAuthLoading(true);
    try {
      // Add your signup logic here
      console.log("Signup:", signupData);
      setAuthModal(null);
    } catch (err) {
      setAuthError(err.message || "Registration failed");
    } finally {
      setAuthLoading(false);
    }
  };

  const switchAuthMode = (mode) => {
    setAuthError("");
    setAuthModal(mode);
  };

  const closeAuthModal = () => {
    setAuthModal(null);
    setAuthError("");
    setLoginData({ email: "", password: "" });
    setSignupData({ name: "", email: "", password: "", confirmPassword: "" });
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F8F5F0]/95 shadow-lg shadow-[#8A9778]/5 backdrop-blur-xl"
          : "bg-[#F8F5F0]/80 backdrop-blur-md"
      }`}
    >
      {/* Announcement bar */}
      <div className="bg-gradient-to-r from-[#8A9778] via-[#8A9778] to-[#D4A45A]">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-center text-[11px] sm:text-xs font-medium text-white leading-normal">
          <span className="text-[#F8F5F0] shrink-0 text-[10px] sm:text-xs">✦</span>
          <span className="truncate sm:overflow-visible sm:whitespace-normal">
            New cohorts open every week — learn with experts and grow faster.
          </span>
          <Link
            to="/courses"
            className="shrink-0 whitespace-nowrap inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 sm:px-3 py-0.5 text-[10px] sm:text-[11px] font-semibold text-white no-underline transition hover:bg-white/30"
          >
            Explore <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </div>

      {/* Main nav */}
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 border-b border-gray-300">
        <div className="flex h-16 sm:h-20 items-center justify-between">
          {/* Logo */}
          <BrandLogo to="/" size="md" />

          {/* Desktop menu */}
          <ul className="hidden items-center gap-1 lg:flex">
            {currentMenu.map((item) =>
              item.dropdown ? (
                <li key={item.name} className="relative">
                  <button
                    onMouseEnter={() => setDropdownOpen(item.name)}
                    onMouseLeave={() => setDropdownOpen(null)}
                    className="flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                  >
                    {item.name}
                    <FiChevronDown
                      size={ICON.sm}
                      strokeWidth={2}
                      className={`transition-transform duration-200 ${dropdownOpen === item.name ? "rotate-180 text-[#8A9778]" : ""}`}
                    />
                  </button>
                  <div
                    onMouseEnter={() => setDropdownOpen(item.name)}
                    onMouseLeave={() => setDropdownOpen(null)}
                    className={`absolute left-0 top-full pt-2 transition-all duration-200 ${
                      dropdownOpen === item.name
                        ? "pointer-events-auto translate-y-0 opacity-100"
                        : "pointer-events-none translate-y-2 opacity-0"
                    }`}
                  >
                    <div className="w-56 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl shadow-slate-900/10">
                      <div className="p-1.5">
                        {item.dropdown.map((d) => {
                          const DropIcon = d.icon;
                          return (
                            <Link
                              key={d.path}
                              to={d.path}
                              className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-600 no-underline transition hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                            >
                              <span className={iconWrap}>
                                <DropIcon size={ICON.md} strokeWidth={2} />
                              </span>
                              {d.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </li>
              ) : (
                <li key={item.name}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `block rounded-xl px-3 py-2 text-sm font-medium transition ${
                        isActive
                          ? "bg-[#8A9778]/10 text-[#6B7760]"
                          : "text-slate-600 hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                      }`
                    }
                  >
                    {item.name}
                  </NavLink>
                </li>
              ),
            )}
          </ul>
           
          {/* Search Bar & Auth */}
          <div className="relative hidden lg:flex items-center gap-4" ref={searchRef}>
            {!user && (
              <div className="flex items-center">
                <span
                  onClick={() => navigate("/signup")}
                  className="rounded-xl px-2 py-2.5 text-sm font-semibold text-slate-700 no-underline transition hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                > 
                  Sign up
                </span>
                /
                <span
                  onClick={() => navigate("/login")}
                  className="rounded-xl px-2  py-2.5 text-sm font-semibold text-slate-700 no-underline transition hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                >
                  Login
                </span>
              </div>
            )}
            <form onSubmit={handleSearch} className="flex items-center">
              <div className="relative">
                <FiSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={16}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  placeholder="Search courses..."
                  className="w-72 h-[45px] rounded-3xl border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm text-slate-700 placeholder:text-slate-400 transition-all focus:border-[#8A9778] focus:ring-2 focus:ring-[#8A9778]/20"
                />
              </div>
            </form>

            

            {/* Search Dropdown */}
            {searchOpen &&
              (searchQuery ||
                searchResults.courses.length > 0 ||
                searchResults.categories.length > 0) && (
                <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl shadow-slate-900/10">
                  {searchResults.categories.length > 0 && (
                    <div className="border-b border-slate-100 p-2">
                      <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        Categories
                      </p>
                      {searchResults.categories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => handleCategoryClick(cat.name)}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                        >
                          <FiTag size={14} className="text-[#8A9778]" />
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {searchResults.courses.length > 0 && (
                    <div className="max-h-64 overflow-y-auto p-2">
                      <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        Courses
                      </p>
                      {searchResults.courses.map((course) => (
                        <button
                          key={course.id}
                          onClick={() => handleCourseClick(course.id)}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition hover:bg-[#F8F5F0]"
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F8F5F0] text-[#8A9778]">
                            <FiBookOpen size={14} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium text-slate-700">
                              {course.title}
                            </p>
                            <p className="truncate text-xs text-slate-400">
                              {course.category_name}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchQuery &&
                    searchResults.courses.length === 0 &&
                    searchResults.categories.length === 0 && (
                      <div className="p-4 text-center text-sm text-slate-400">
                        No results found
                      </div>
                    )}
                  <div className="border-t border-slate-100 bg-slate-50 p-2">
                    <button
                      type="submit"
                      form="no-form"
                      onClick={handleSearch}
                      className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[#8A9778] transition hover:bg-[#8A9778]/10"
                    >
                      <FiSearch size={14} /> Search all courses
                    </button>
                  </div>
                </div>
              )}
          </div>

          {/* Desktop CTA - User Profile */}
          {user && (
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen((p) => !p)}
                className="flex items-center gap-2.5 rounded-2xl border border-[#8A9778]/20 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-[#8A9778]/40 hover:shadow-md"
              >
                <span className={`${UI.avatarSm} h-8 w-8 text-xs`}>
                  {user.name?.[0]?.toUpperCase() ?? "U"}
                </span>
                <FiChevronDown
                  size={ICON.sm}
                  strokeWidth={2}
                  className={`transition-transform ${profileOpen ? "rotate-180" : ""}`}
                />
              </button>

              {profileOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl shadow-slate-900/10">
                  <div className="border-b border-slate-100 px-4 py-3">
                    <p className="text-xs text-slate-400">Signed in as</p>
                    <p className="truncate text-sm font-semibold text-slate-800">
                      {user.name ?? "User"}
                    </p>
                  </div>
                  <div className="p-1.5">
                    <Link
                      to={dashboardPath}
                      className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-slate-600 no-underline transition hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                    >
                      <FiGrid
                        size={ICON.sm}
                        strokeWidth={2}
                        className="text-[#8A9778]"
                      />{" "}
                      Dashboard
                    </Link>
                    {bookingsPath && (
                      <Link
                        to={bookingsPath}
                        className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-slate-600 no-underline transition hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                      >
                        <FiBookmark
                          size={ICON.sm}
                          strokeWidth={2}
                          className="text-[#8A9778]"
                        />{" "}
                        My Bookings
                      </Link>
                    )}
                    <Link
                      to={`${dashboardPath.replace(/\/dashboard$/, "")}/profile`}
                      className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-slate-600 no-underline transition hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                    >
                      <FiUser
                        size={ICON.sm}
                        strokeWidth={2}
                        className="text-[#8A9778]"
                      />{" "}
                      Profile
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setProfileOpen(false);
                        setShowLogoutModal(true);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-rose-500 transition hover:bg-rose-50 cursor-pointer"
                    >
                      <FiLogOut size={ICON.sm} strokeWidth={2} /> Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((p) => !p)}
            className={`${UI.iconBtn} lg:hidden`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <FiX size={ICON.lg} strokeWidth={2} />
            ) : (
              <FiMenu size={ICON.lg} strokeWidth={2} />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`overflow-hidden border-t border-slate-100 transition-all duration-300 lg:hidden ${
          mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="mx-auto max-w-7xl space-y-1 px-4 py-4 sm:px-6">
          {currentMenu.map((item) =>
            item.dropdown ? (
              <div key={item.name}>
                <button
                  type="button"
                  onClick={() =>
                    setDropdownOpen((p) => (p === item.name ? null : item.name))
                  }
                  className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  {item.name}
                  <FiChevronDown
                    size={ICON.sm}
                    strokeWidth={2}
                    className={`transition-transform ${dropdownOpen === item.name ? "rotate-180 text-[#8A9778]" : ""}`}
                  />
                </button>
                {dropdownOpen === item.name && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-[#8A9778]/20 pl-4">
                    {item.dropdown.map((d) => {
                      const DropIcon = d.icon;
                      return (
                        <Link
                          key={d.path}
                          to={d.path}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-slate-600 no-underline transition hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                        >
                          <DropIcon
                            size={ICON.sm}
                            strokeWidth={2}
                            className="text-[#8A9778]"
                          />{" "}
                          {d.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.name}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block rounded-2xl px-4 py-3 text-sm font-semibold no-underline transition ${
                    isActive
                      ? "bg-[#8A9778]/10 text-[#6B7760]"
                      : "text-slate-700 hover:bg-[#F8F5F0] hover:text-[#8A9778]"
                  }`
                }
              >
                {item.name}
              </NavLink>
            ),
          )}

          <div className={`mt-4 grid gap-3 border-t border-slate-100 pt-4 ${user ? "grid-cols-1" : "grid-cols-2"}`}>
            {user ? (
              <>
                <Link
                  to={dashboardPath}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-2xl border border-[#8A9778]/20 px-4 py-3 text-sm font-semibold text-slate-700 no-underline transition hover:border-[#8A9778]/40 hover:text-[#8A9778]"
                >
                  <FiGrid size={15} className="text-[#8A9778]" /> Dashboard
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    setShowLogoutModal(true);
                  }}
                  className="flex items-center justify-center gap-2 rounded-2xl border border-rose-200 px-4 py-3 text-sm font-semibold text-rose-500 transition hover:bg-rose-50 cursor-pointer"
                >
                  <FiLogOut size={15} /> Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center rounded-2xl border border-[#8A9778]/20 px-4 py-3 text-sm font-semibold text-slate-700 no-underline transition hover:border-[#8A9778]/40 hover:text-[#8A9778]"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center rounded-2xl bg-gradient-to-r from-[#8A9778] to-[#6B7760] px-4 py-3 text-sm font-semibold text-white no-underline shadow-lg shadow-[#8A9778]/25 transition hover:opacity-90"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* SweetAlert Style Logout Confirmation Modal */}
      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleConfirmLogout}
        title="Sign Out Confirmation"
        message="Are you sure you want to sign out of your account? You will need to log back in to access your portal."
        confirmText="Yes, Sign Out"
        cancelText="Cancel"
        type="danger"
        loading={loggingOut}
      />
    </header>
  );
}
